import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, ZAR, formatApiErrorDetail } from "@/lib/api";
import { ShoppingBag, Plus, Utensils, Package, Lock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const cats = [
  { key: "all", label: "All" },
  { key: "food", label: "Food & Groceries" },
  { key: "general", label: "Essentials" },
];

export default function Marketplace() {
  const { refresh } = useAuth();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("all");
  const [available, setAvailable] = useState(0);
  const [canSpend, setCanSpend] = useState(false);
  const [status, setStatus] = useState("approved");
  const [buying, setBuying] = useState(null);

  // manual log
  const [manual, setManual] = useState({ description: "", amount: "", category: "general", merchant: "" });
  const [open, setOpen] = useState(false);
  const [logging, setLogging] = useState(false);

  const loadCredit = async () => {
    const { data } = await api.get("/credit");
    setAvailable(data.credit.credit_available);
    setCanSpend(data.credit.can_spend);
    setStatus(data.credit.verification_status);
  };
  const loadProducts = async () => {
    const q = cat === "all" ? "" : `?category=${cat}`;
    const { data } = await api.get(`/products${q}`);
    setProducts(data.products);
  };

  useEffect(() => { loadCredit(); }, []);
  useEffect(() => { loadProducts(); }, [cat]);

  const buy = async (p) => {
    setBuying(p.id);
    try {
      const { data } = await api.post(`/products/${p.id}/purchase`);
      setAvailable(data.credit.credit_available);
      await refresh();
      toast.success(`Purchased ${p.name} · earned ${Math.floor(p.price)} points`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBuying(null);
    }
  };

  const logManual = async () => {
    if (!manual.description || !manual.amount) return toast.error("Add a description and amount");
    setLogging(true);
    try {
      const { data } = await api.post("/transactions", {
        description: manual.description,
        amount: parseFloat(manual.amount),
        category: manual.category,
        merchant: manual.merchant || "Manual entry",
      });
      setAvailable(data.credit.credit_available);
      await refresh();
      setOpen(false);
      setManual({ description: "", amount: "", category: "general", merchant: "" });
      toast.success("Transaction logged");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLogging(false);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-xs font-medium tracking-overline text-primary uppercase mb-2">Marketplace</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Spend your credit</h1>
        </div>
        <div className="rounded-2xl border-2 border-border bg-card px-5 py-3">
          <p className="text-xs tracking-overline uppercase text-muted-foreground">Available</p>
          <p className="font-mono text-xl font-bold text-primary" data-testid="market-available">{ZAR(available)}</p>
        </div>
      </div>

      {!canSpend && (
        <div className="mb-6 rounded-2xl border-2 border-sunny bg-sunny/15 p-4 flex items-center gap-3" data-testid="market-locked-banner">
          <Lock className="text-[#141414]/70 shrink-0" size={18} />
          <p className="text-sm">
            {status === "pending"
              ? "Your account is under review. Spending unlocks once an admin approves your documents."
              : "Verify your profile to unlock spending. Upload your documents on the Profile page."}
          </p>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div className="flex gap-2 flex-wrap">
          {cats.map((cc) => (
            <button
              key={cc.key}
              onClick={() => setCat(cc.key)}
              data-testid={`cat-${cc.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-colors duration-150 ${
                cat === cc.key ? "border-primary bg-primary text-white" : "border-border hover:border-primary"
              }`}
            >
              {cc.label}
            </button>
          ))}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="log-txn-btn"
              disabled={!canSpend}
              className="px-4 py-2 rounded-full text-sm font-medium border-2 border-border hover:border-primary inline-flex items-center gap-2 transition-colors duration-150 disabled:opacity-40"
            >
              <Plus size={16} /> Log a purchase
            </button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle>Log a manual purchase</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <input
                placeholder="Description" value={manual.description}
                onChange={(e) => setManual({ ...manual, description: e.target.value })}
                data-testid="manual-desc-input"
                className="w-full bg-secondary border border-border px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
              <input
                placeholder="Merchant (optional)" value={manual.merchant}
                onChange={(e) => setManual({ ...manual, merchant: e.target.value })}
                data-testid="manual-merchant-input"
                className="w-full bg-secondary border border-border px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number" placeholder="Amount (R)" value={manual.amount}
                  onChange={(e) => setManual({ ...manual, amount: e.target.value })}
                  data-testid="manual-amount-input"
                  className="w-full bg-secondary border border-border px-3.5 py-3 text-sm font-mono outline-none focus:ring-2 focus:ring-primary"
                />
                <select
                  value={manual.category}
                  onChange={(e) => setManual({ ...manual, category: e.target.value })}
                  data-testid="manual-category-select"
                  className="w-full bg-secondary border border-border px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="general">Essentials</option>
                  <option value="food">Food & Groceries</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <button
                onClick={logManual} disabled={logging}
                data-testid="manual-submit-btn"
                className="bg-primary text-white font-medium px-5 py-2.5 disabled:opacity-60"
              >
                {logging ? "Logging…" : "Log purchase"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
        {products.map((p, i) => {
          const affordable = p.price <= available;
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              className="rounded-2xl border-2 border-border bg-card overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(211,23,43,0.08)] transition-all duration-200 flex flex-col"
              data-testid={`product-${p.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-secondary relative">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 text-[10px] font-bold tracking-overline uppercase bg-white/90 rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                  {p.category === "food" ? <Utensils size={11} /> : <Package size={11} />}
                  {p.category === "food" ? "Food" : "Essentials"}
                </span>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-bold">{p.name}</h3>
                <p className="text-xs text-muted-foreground mt-1 flex-1">{p.description}</p>
                <p className="text-xs text-muted-foreground font-mono mt-3">{p.merchant}</p>
                <div className="flex items-center justify-between mt-4">
                  <p className="font-mono text-xl font-bold">{ZAR(p.price)}</p>
                  <button
                    onClick={() => buy(p)}
                    disabled={!affordable || !canSpend || buying === p.id}
                    data-testid={`buy-btn-${p.id}`}
                    className="px-4 py-2 rounded-full text-sm font-semibold bg-primary text-white inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform duration-150 disabled:opacity-40 disabled:hover:translate-y-0"
                  >
                    <ShoppingBag size={15} /> {buying === p.id ? "…" : !canSpend ? "Locked" : affordable ? "Buy" : "Low credit"}
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
